import { isBefore, addDays, format } from 'date-fns';
import { URPActionDays } from '@ourbranch/lookups';
import { DynamoDbPolicy } from '@ourbranch/schema-types/dynamodb';

/**
 * Last day of the URP (Underwriting Review Period) window, as yyyy-MM-dd.
 * The effective date is day 1, so the last day is effectiveDate + URP days - 1.
 * @param policy - The policy to check
 * @returns the URP end date, or null when the state has no URP for this policy type (e.g. farm)
 */
export const getURPEndDate = (
  policy: Pick<DynamoDbPolicy, 'state' | 'policyType' | 'effectiveDate'>
): string | null => {
  const stateConfig = URPActionDays[policy.state as keyof typeof URPActionDays];
  if (!stateConfig) {
    console.error(`Unsupported state for URP calculation: ${policy.state}`);
    return null;
  }

  const urpDays = stateConfig[policy.policyType as keyof typeof stateConfig];
  if (urpDays === undefined) {
    console.error(`Unsupported policy type '${policy.policyType}' for state '${policy.state}' in URP calculation`);
    return null;
  }

  if (urpDays === null) {
    return null;
  }

  return format(addDays(new Date(`${policy.effectiveDate}T00:00:00`), urpDays - 1), 'yyyy-MM-dd');
};

/**
 * Determines if a policy is within the URP (Underwriting Review Period) window
 * @param policy - The policy to check
 * @param currentDate - Optional current date (defaults to today)
 * @returns boolean indicating if policy is within URP window
 */
export const isWithinURPWindow = (policy: DynamoDbPolicy, currentDate?: Date): boolean => {
  const today = currentDate ? new Date(currentDate) : new Date();
  today.setHours(0, 0, 0, 0);

  const stateConfig = URPActionDays[policy.state as keyof typeof URPActionDays];
  if (!stateConfig) {
    console.error(`Unsupported state for URP calculation: ${policy.state}`);
    return false;
  }

  const urpDays = stateConfig[policy.policyType as keyof typeof stateConfig];
  if (urpDays === undefined) {
    console.error(`Unsupported policy type '${policy.policyType}' for state '${policy.state}' in URP calculation`);
    return false;
  }

  if (urpDays === null) {
    return false;
  }

  return isBefore(today, addDays(new Date(`${policy.effectiveDate}T00:00:00`), urpDays));
};

/**
 * Determines if a policy is in URP and is first term
 * @param policy - The policy to check
 * @param currentDate - Optional current date (defaults to today)
 * @returns boolean indicating if policy is in URP first term
 */
export const isInURP = (policy: DynamoDbPolicy, currentDate?: Date): boolean => {
  return isWithinURPWindow(policy, currentDate) && policy.term === 1;
};
